import { useEffect, useMemo, useState } from 'react';
import {
  AUXILIARY_TARGETS,
  HEALTH_TARGETS,
  HealthStatus,
  HealthTarget,
  hasAuthenticatedSession,
  probeTarget,
} from '@/config/portalHealth';

type HealthMap = Record<string, HealthStatus>;

const INITIAL: HealthMap = [...HEALTH_TARGETS, ...AUXILIARY_TARGETS].reduce((acc, t) => {
  acc[t.portalId] = 'checking';
  return acc;
}, {} as HealthMap);

/**
 * 并发探测所有 portal 前端 + 后端健康状态。
 * 挂载时立即发起一次，返回 portalId → 状态 的 map。
 */
export function usePortalHealth(intervalMs = 0): {
  healthMap: HealthMap;
  refresh: () => void;
} {
  const [healthMap, setHealthMap] = useState<HealthMap>(INITIAL);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const all: HealthTarget[] = [...HEALTH_TARGETS, ...AUXILIARY_TARGETS];
    setHealthMap(INITIAL);

    const probeAll = async () => {
      const authenticated = await hasAuthenticatedSession();
      const entries = await Promise.all(
        all.map(async (target) => [
          target.portalId,
          await probeTarget(target, 4000, authenticated),
        ] as const),
      );
      if (!cancelled) setHealthMap(Object.fromEntries(entries));
    };

    void probeAll();
    return () => {
      cancelled = true;
    };
  }, [tick]);

  useEffect(() => {
    if (!intervalMs) return;
    const timer = setInterval(() => setTick((x) => x + 1), intervalMs);
    return () => clearInterval(timer);
  }, [intervalMs]);

  const api = useMemo(
    () => ({
      healthMap,
      refresh: () => setTick((x) => x + 1),
    }),
    [healthMap],
  );

  return api;
}
